// Whole-app language toggle (EN/CH/JP, footer). This covers the app's own UI chrome — buttons,
// titles, labels, tooltips, error/confirm text, info bubbles — including the "Selected text from
// the document:"/"Critique text from the document:" bubble labels (chat.selectedTextLabel/
// critiqueTextLabel), which used to be chosen by detecting the selected passage's own language
// (chatPrompts.ts's old isMostlyChineseText) rather than this toggle — that detection was removed
// 2026-08-12 per explicit request, along with the languageDirective() that told the AI which
// language to reply in. The AI's own reply language is not steered by this toggle (or anything
// else) — a message is sent as-is and the model decides; see chatPrompts.ts's own top comment.
// Legal pages (privacy/terms/support.html) are explicitly out of scope too, per explicit
// decision — separate static documents with their own review bar.
//
// Architecture: every UI string lives in the `Strings` interface below, with one EN and one ZH
// object satisfying it — TypeScript itself guarantees the two can never drift out of parity
// (a key present in one but not the other is a compile error), which matters far more here than
// for a typical config object, since a half-translated UI would be a real, silent regression.
// Dynamic strings (pluralization, interpolated names/numbers) are typed as functions rather than
// plain strings, computed per-language since English and Chinese pluralize differently (Chinese
// needs no plural form at all).

export type Language = "en" | "zh" | "ja";

const LANGUAGE_KEY = "wai.language";

export function getLanguage(): Language {
  const stored = localStorage.getItem(LANGUAGE_KEY);
  return stored === "zh" || stored === "ja" ? stored : "en";
}

export function setLanguage(lang: Language): void {
  localStorage.setItem(LANGUAGE_KEY, lang);
}

export interface Strings {
  common: {
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    add: string;
    export: string;
    backToChat: string;
    yesDelete: string;
  };
  standalone: {
    noticeBody: string;
    noticeWarning: string;
    noticeLearnMore: string;
    noticeDismiss: string;
  };
  onboarding: {
    title: string;
    body: string;
    addProvider: string;
  };
  providerList: {
    title: string;
    emptyHint: string;
    startNewChat: string;
    addProvider: string;
    deleteConfirm: (chatCount: number) => string;
    defaultValue: string;
    temperature: (value: string) => string;
    // "none" — an explicitly-set value, no suffix. "model" — unset, resolved from this specific
    // model (live API data or the name-pattern table) — same "model default" wording providerForm.ts/
    // sessionSwitcher.ts use for the analogous placeholder. "generic" — unset, resolved from the flat
    // conservative-floor fallback, not really tied to this model at all — same distinction those two
    // screens already make via isModelSpecificMaxContext, extended here so this card doesn't overstate
    // an unrelated generic number as if it were the model's own default.
    maxContext: (value: string, defaultKind: "none" | "model" | "generic") => string;
    infoHeading: string;
    infoBody: string;
  };
  providerForm: {
    titleAdd: string;
    titleEdit: string;
    displayName: string;
    aiService: string;
    adapterType: string;
    adapterOpenAiCompatible: string;
    adapterGemini: string;
    adapterAnthropic: string;
    apiEndpoint: string;
    showEndpointListAria: string;
    apiKey: string;
    apiKeyInfo: string;
    getApiKey: string;
    model: string;
    modelPlaceholder: string;
    temperature: string;
    temperatureInfo: string;
    maxContext: string;
    maxContextInfo: string;
    systemPrompt: string;
    systemPromptInfo: string;
    systemPromptPlaceholder: string;
    maxOutput: string;
    maxOutputInfo: string;
    modelDefault: (value: string) => string;
    genericDefault: (value: string) => string;
    fetchBlockedReason: string;
    allFieldsRequired: string;
    modelNotInList: (model: string) => string;
    temperatureRange: string;
    maxContextPositive: string;
    maxOutputPositive: string;
    saveConfirmCost: (name: string) => string;
    saveConfirmAgree: string;
    agreeSaveProvider: string;
  };
  sessionSwitcher: {
    homeTitle: string;
    switchChatAria: string;
    newChatTitle: string;
    newChatAria: string;
    editChatIconTitle: string;
    editChatIconAria: string;
    deleteChatIconTitle: string;
    deleteChatIconAria: string;
    deleteChatConfirmText: string;
    navHistory: string;
    navProviders: string;
    navStoryBible: string;
    settingsIconTitle: string;
    settingsIconAria: string;
    startNewChatTitle: string;
    chatNameOptional: string;
    chatNamePlaceholder: (example: string) => string;
    provider: string;
    model: string;
    modelPlaceholder: string;
    startChat: string;
    enterOrSelectModel: string;
    modelNotInList: (model: string) => string;
    temperature: string;
    temperatureInfo: string;
    maxContext: string;
    maxContextInfo: string;
    maxOutput: string;
    maxOutputInfo: string;
    systemPrompt: string;
    systemPromptInfo: string;
    inherit: string;
    clear: string;
    modelDefault: string;
    genericDefault: string;
    providerDefault: (value: string) => string;
    providerDefaultPlain: string;
    noSystemPrompt: string;
    none: string;
    temperatureRange: string;
    maxContextPositive: string;
    maxOutputPositive: string;
    editChatTitle: string;
    chatName: string;
    noProvidersHint: string;
    chatNameRequired: string;
    chatNoLongerExists: string;
    chatUpdatedElsewhere: string;
    selectProviderPlaceholder: string;
    selectProviderFirstHint: string;
    providerRequired: string;
  };
  storyBible: {
    fieldLabel: string;
    none: string;
    create: string;
    edit: string;
    titleNew: string;
    titleEdit: string;
    nameLabel: string;
    entries: string;
    entriesInfo: string;
    tokensTotal: (value: string) => string;
    tokensCount: (value: string) => string;
    noEntries: string;
    addEntry: string;
    entryTitle: string;
    entryText: string;
    saveEntry: string;
    includedTitle: string;
    excludedTitle: string;
    includedAria: string;
    excludedAria: string;
    titleRequired: string;
    entryTextRequired: string;
    nameRequired: string;
    listTitle: string;
    listEmptyHint: string;
    entryCount: (n: number) => string;
    linkedToChats: (titles: string[], moreCount: number) => string;
    notLinkedToAnyChat: string;
    addStoryBible: string;
    pendingEntryNote: string;
    expandAll: string;
    linkedChatsHeading: string;
    linkedChatsNone: string;
    closedTag: string;
    unlink: string;
    link: string;
    linkSelectPlaceholder: string;
    unlinkedOption: (chatTitle: string) => string;
    linkedElsewhereOption: (chatTitle: string, bibleName: string) => string;
    collapseAll: string;
  };
  history: {
    title: string;
    tabChats: string;
    tabStoryBibles: string;
    emptyChats: string;
    unknownProvider: string;
    messageCount: (n: number) => string;
    purgeChatConfirm: string;
    emptyDeletedBibles: string;
    purgeConfirm: string;
    yesPurge: string;
    recover: string;
    purge: string;
    selectedCount: (n: number) => string;
    selectItemAria: (title: string) => string;
    recoverSelected: string;
    purgeSelected: string;
    purgeAll: string;
    purgeSelectedChatsConfirm: (n: number) => string;
    purgeAllChatsConfirm: (n: number) => string;
    purgeSelectedBiblesConfirm: (n: number) => string;
    purgeAllBiblesConfirm: (n: number) => string;
  };
  chat: {
    sessionTag: (provider: string, model: string, temperature: string) => string;
    defaultValue: string;
    showToolbar: string;
    hideToolbar: string;
    totalTokensLabel: string;
    tokenUsageAria: string;
    contextExceededBanner: string;
    dismissAria: string;
    staleChatBanner: string;
    reload: string;
    regenerateBanner: string;
    cancelRegenerateAria: string;
    typeMessagePlaceholder: string;
    send: string;
    thinking: string;
    contextExceededDivider: string;
    providerChangedDivider: (name: string) => string;
    responseTime: (seconds: string) => string;
    tokensInOut: (inTokens: string, outTokens: string) => string;
    expandReply: string;
    collapseReply: string;
    deleteExchangeTitle: string;
    deleteReplyConfirmAria: string;
    deleteReplyConfirmText: string;
    regenerateLabel: string;
    tokenUsageHeading: string;
    inputTokensLabel: string;
    outputTokensLabel: string;
    totalTokensRowLabel: string;
    contextWindowLabel: string;
    tokenUsageInfo: string;
    staleChatGeneric: string;
    staleChatRegenerating: string;
    excludeHint: string;
    includeHint: string;
    critiqueThis: string;
    alreadyCritiqued: string;
    attached: string;
    excluded: string;
    removeSelectionAria: string;
    providerGoneError: string;
    regenerateFeedbackPlaceholder: string;
    busyRegenerateError: string;
    busyDeleteError: string;
    typeOrSelectFirst: string;
    replyCancelled: string;
    sendError: string;
    regenerationCancelled: string;
    regenerateError: string;
    collapseSelectedText: string;
    expandSelectedText: string;
    selectedTextLabel: string;
    critiqueTextLabel: string;
  };
  exportFile: {
    provider: string;
    model: string;
    exported: string;
    you: string;
    assistant: string;
    untitledEntry: string;
    excludedFromContext: string;
  };
  modelCombobox: {
    showModelListAria: string;
    noModelsReturned: string;
    fetchFailed: string;
  };
  domUtils: {
    moreInfoAria: string;
  };
  settings: {
    title: string;
    critiquePromptLabel: string;
    critiquePromptInfo: string;
    revertToDefault: string;
    languageLabel: string;
    languageEnglish: string;
    languageChinese: string;
    languageJapanese: string;
  };
}

export const EN: Strings = {
  common: {
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    export: "Export",
    backToChat: "← Back to chat",
    yesDelete: "Yes, delete",
  },
  standalone: {
    noticeBody:
      "All data in NovelChat Web, including providers, chats, and your Story Bible, is stored only in this browser on this computer. Nothing is stored online, and it does not sync with the Word add-in. Clearing your browser's site data, or using a private or incognito window, will permanently erase it.",
    noticeWarning: "Avoid using this on public or shared computers.",
    noticeLearnMore: "Learn more",
    noticeDismiss: "Got it",
  },
  onboarding: {
    title: "Welcome",
    body: "No AI providers are set up yet. Add one to start chatting.",
    addProvider: "+ Add provider",
  },
  providerList: {
    title: "AI Providers",
    emptyHint: "No providers configured yet.",
    startNewChat: "Start new chat",
    addProvider: "+ Add provider",
    deleteConfirm: (chatCount) =>
      chatCount > 0
        ? `Delete this provider? This cannot be undone. ${chatCount} chat${chatCount === 1 ? "" : "s"} currently ${chatCount === 1 ? "uses" : "use"} it and will need to be reconnected to a different provider afterward via Edit chat.`
        : "Delete this provider? This cannot be undone.",
    defaultValue: "default",
    temperature: (value) => `Temperature: ${value}`,
    maxContext: (value, defaultKind) =>
      `Max context: ${value}${defaultKind === "model" ? " (model default)" : defaultKind === "generic" ? " (default)" : ""}`,
    infoHeading: "Get more from AI",
    infoBody:
      "NovelChat is built to support your novel writing and make your AI workflow faster and more effective. AI works best when you guide it clearly. Setting up a System prompt, building a Story Bible, and writing clear instructions all help you get better results.",
  },
  providerForm: {
    titleAdd: "Add provider",
    titleEdit: "Edit provider",
    displayName: "Display name",
    aiService: "AI service",
    adapterType: "Adapter type",
    adapterOpenAiCompatible: "OpenAI-compatible",
    adapterGemini: "Gemini",
    adapterAnthropic: "Anthropic (Claude)",
    apiEndpoint: "API endpoint",
    showEndpointListAria: "Show known endpoints",
    apiKey: "API key",
    apiKeyInfo:
      "This key lets NovelChat send requests to this AI provider on your behalf and receive replies. It's stored locally on this computer. Any usage costs are billed by the provider directly, not NovelChat.",
    getApiKey: "Get an API key",
    model: "Model",
    modelPlaceholder: "Select or enter a model",
    temperature: "Temperature (optional)",
    temperatureInfo:
      "Controls response variability, from 0 to 2. Lower values (e.g., 0.3) produce more focused, consistent output — useful for maintaining a consistent tone or character voice. Higher values (e.g., 0.9) produce more varied, creative output — useful for brainstorming. Leave blank to use the model's default.",
    maxContext: "Max context (tokens, optional)",
    maxContextInfo:
      "Controls how much of this chat's history is sent with each message — a higher number helps the AI remember more of a long scene, but costs more per message. Leave blank and NovelChat automatically picks the right value for your model. Only raise it manually if you're sure of your model's real limit — going over that limit will cause request errors.",
    systemPrompt: "System prompt (optional)",
    systemPromptInfo:
      "Sent before every message in every chat using this provider. Use this to define a persona, tone, or language preference the AI should follow. Leave blank to use none.",
    systemPromptPlaceholder:
      "Optional — describe a persona, tone, or instructions for the AI to follow in every chat using this provider.",
    maxOutput: "Max reply length (tokens, optional)",
    maxOutputInfo:
      "Anthropic (Claude) requires a maximum reply length on every request. Leave blank and NovelChat automatically uses your model's real limit, so this rarely needs adjusting. Set a lower number yourself only if you want to cap reply length and control cost.",
    modelDefault: (value) => `Model default: ${value}`,
    genericDefault: (value) => `Default: ${value}`,
    fetchBlockedReason:
      "Enter the API endpoint and API key first, then click the arrow to load models.",
    allFieldsRequired: "All fields are required.",
    modelNotInList: (model) =>
      `"${model}" isn't in the fetched model list for this provider. Pick one from the dropdown (▾), or re-fetch if the list looks outdated.`,
    temperatureRange: "Temperature must be a number between 0 and 2, or left blank.",
    maxContextPositive: "Max context (tokens) must be a positive whole number, or left blank.",
    maxOutputPositive: "Max reply length (tokens) must be a positive whole number, or left blank.",
    saveConfirmCost: (name) =>
      `Using this API key will send requests to ${name} and may incur usage costs, billed directly by them according to your own account and pricing plan. NovelChat has no visibility into or control over these costs — check that provider's own website for current pricing before proceeding.`,
    saveConfirmAgree: `By clicking "I Agree, Save Provider" below, you acknowledge and accept full responsibility for any usage costs incurred through this API key.`,
    agreeSaveProvider: "I Agree, Save Provider",
  },
  sessionSwitcher: {
    homeTitle: "Home",
    switchChatAria: "Switch chat",
    newChatTitle: "Start a new chat",
    newChatAria: "New chat",
    editChatIconTitle: "Edit this chat",
    editChatIconAria: "Edit chat",
    deleteChatIconTitle: "Delete this chat",
    deleteChatIconAria: "Delete",
    deleteChatConfirmText: "Delete this chat? You can recover it later from History.",
    navHistory: "History",
    navProviders: "Providers",
    navStoryBible: "Story Bible",
    settingsIconTitle: "Settings",
    settingsIconAria: "Settings",
    startNewChatTitle: "Start a new chat",
    chatNameOptional: "Chat name (optional)",
    chatNamePlaceholder: (example) => `Leave blank to default to a timestamp, e.g. ${example}`,
    provider: "Provider",
    model: "Model",
    modelPlaceholder: "Select or enter a model",
    startChat: "Start chat",
    enterOrSelectModel: "Enter or select a model.",
    modelNotInList: (model) =>
      `"${model}" isn't in the fetched model list. Pick one from the dropdown (▾), or re-fetch if the list looks outdated.`,
    temperature: "Temperature",
    temperatureInfo:
      "Controls response variability, from 0 to 2. Lower values (e.g., 0.3) produce more focused, consistent output. Higher values (e.g., 0.9) produce more varied, creative output. Inherit follows this chat's provider setting, even if you switch providers later. Clear ignores the provider too and lets the model use its own default instead.",
    maxContext: "Max context (tokens)",
    maxContextInfo:
      "Controls how much of this chat's history is sent with each message. Inherit follows this chat's provider setting, even if you switch providers later. Clear ignores the provider and always uses your model's real limit instead. If you set your own number and later switch providers, it stays as you set it — unless it's too high for the new model, in which case it's automatically lowered to fit.",
    maxOutput: "Max reply length (tokens, optional)",
    maxOutputInfo:
      "Anthropic (Claude) requires a maximum reply length on every request. Leave blank to use this chat's provider setting, which is already your model's real limit — this rarely needs adjusting. Only applies to Anthropic providers. If you set your own number and later switch providers, it's reset back to following the new provider.",
    systemPrompt: "System prompt",
    systemPromptInfo:
      "Sent before every message in this chat. Inherit follows this chat's provider setting, even if you switch providers later. Clear means no system prompt for this chat at all, even if the provider has one. Deleting what you've typed here also counts as Clear — click Inherit if you want to go back to following the provider instead.",
    inherit: "inherit",
    clear: "clear",
    modelDefault: "Model default",
    genericDefault: "Default",
    providerDefault: (value) => `Provider default: ${value}`,
    providerDefaultPlain: "Provider default",
    noSystemPrompt: "No system prompt (cleared)",
    none: "none",
    temperatureRange: "Temperature must be a number between 0 and 2, or left blank.",
    maxContextPositive: "Max context (tokens) must be a positive whole number, or left blank.",
    maxOutputPositive: "Max reply length (tokens) must be a positive whole number, or left blank.",
    editChatTitle: "Edit chat",
    chatName: "Chat name",
    noProvidersHint:
      "No providers are set up. Add one first, then come back to reconnect this chat.",
    chatNameRequired: "Chat name is required.",
    chatNoLongerExists: "This chat no longer exists — it may have been deleted elsewhere.",
    chatUpdatedElsewhere:
      "This chat was updated elsewhere — cancel and reopen Edit chat to see the latest version, then try again.",
    selectProviderPlaceholder: "This chat's provider was deleted — select a provider",
    selectProviderFirstHint: "Select a provider above first.",
    providerRequired: "Select a provider for this chat.",
  },
  storyBible: {
    fieldLabel: "Story Bible",
    none: "None",
    create: "+ Create",
    edit: "Edit",
    titleNew: "New Story Bible",
    titleEdit: "Edit Story Bible",
    nameLabel: "Story Bible name",
    entries: "Entries",
    entriesInfo:
      "You can keep everything in a single entry, or split facts across several — whichever is easier to maintain as your story grows. Not sure where to start? Ask an AI chat to help you draft entries, then paste and refine them here.",
    tokensTotal: (value) => `~${value} tokens total`,
    tokensCount: (value) => `~${value} tokens`,
    noEntries: "No entries yet.",
    addEntry: "+ Add entry",
    entryTitle: "Title",
    entryText: "Text",
    saveEntry: "Save entry",
    includedTitle: "Included in context",
    excludedTitle: "Excluded from context",
    includedAria: "Included in context — click to exclude",
    excludedAria: "Excluded from context — click to include",
    titleRequired: "Title is required.",
    entryTextRequired: "Entry text can't be blank.",
    nameRequired: "Story Bible name is required.",
    listTitle: "Story Bibles",
    listEmptyHint: "No Story Bibles yet.",
    entryCount: (n) => `${n} ${n === 1 ? "entry" : "entries"}`,
    linkedToChats: (titles, moreCount) =>
      `Linked to: ${titles.join(", ")}${moreCount > 0 ? ` +${moreCount} more` : ""}`,
    notLinkedToAnyChat: "Not linked to any chat",
    addStoryBible: "+ Add Story Bible",
    pendingEntryNote: "You have an unsaved entry. Save or cancel it before continuing.",
    expandAll: "Expand all",
    linkedChatsHeading: "Linked chats",
    linkedChatsNone: "No chats are linked to this Story Bible yet.",
    closedTag: "(closed)",
    unlink: "Unlink",
    link: "Link",
    linkSelectPlaceholder: "Select a chat to link…",
    unlinkedOption: (chatTitle) => `${chatTitle} (unlinked)`,
    linkedElsewhereOption: (chatTitle, bibleName) => `${chatTitle} (currently: ${bibleName})`,
    collapseAll: "Collapse all",
  },
  history: {
    title: "History",
    tabChats: "Chats",
    tabStoryBibles: "Story Bibles",
    emptyChats:
      "No deleted chats yet. Deleting a chat (the &times; on its tab) moves it here without losing anything.",
    unknownProvider: "Unknown provider",
    messageCount: (n) => `${n} message${n === 1 ? "" : "s"}`,
    purgeChatConfirm: "Purge this chat permanently? This cannot be undone.",
    emptyDeletedBibles:
      "No deleted Story Bibles. Deleting one (from the Story Bibles screen) moves it here without losing anything.",
    purgeConfirm: "Purge this Story Bible permanently? This cannot be undone.",
    yesPurge: "Yes, purge",
    recover: "Recover",
    purge: "Purge",
    selectedCount: (n) => `${n} selected`,
    selectItemAria: (title) => `Select ${title}`,
    recoverSelected: "Recover selected",
    purgeSelected: "Purge selected",
    purgeAll: "Purge all",
    purgeSelectedChatsConfirm: (n) =>
      `Purge ${n} selected chat${n === 1 ? "" : "s"} permanently? This cannot be undone.`,
    purgeAllChatsConfirm: (n) =>
      `Purge all ${n} deleted chat${n === 1 ? "" : "s"} permanently? This cannot be undone.`,
    purgeSelectedBiblesConfirm: (n) =>
      `Purge ${n} selected Story Bible${n === 1 ? "" : "s"} permanently? This cannot be undone.`,
    purgeAllBiblesConfirm: (n) =>
      `Purge all ${n} deleted Story Bible${n === 1 ? "" : "s"} permanently? This cannot be undone.`,
  },
  chat: {
    sessionTag: (provider, model, temperature) =>
      `${provider} · ${model} · Temperature: ${temperature}`,
    defaultValue: "default",
    showToolbar: "Show toolbar",
    hideToolbar: "Hide toolbar",
    totalTokensLabel: "Total Tokens:",
    tokenUsageAria: "Token usage for this chat",
    contextExceededBanner:
      "This chat has exceeded the context limit — older messages are no longer sent to the AI.",
    dismissAria: "Dismiss",
    staleChatBanner:
      "This chat was updated in another window. Reload to see the latest version, or switch to another chat — new or existing.",
    reload: "Reload",
    regenerateBanner:
      "Regenerating that reply — optionally type what should be different below, then press Regenerate.",
    cancelRegenerateAria: "Cancel regenerate",
    typeMessagePlaceholder: "Type your message...",
    send: "Send",
    thinking: "Thinking...",
    contextExceededDivider: "Context exceeded — chat above excluded",
    providerChangedDivider: (name) => `Provider changed to ${name}`,
    responseTime: (seconds) => `Response time: ${seconds}s`,
    tokensInOut: (inTokens, outTokens) => `Tokens: ${inTokens} in / ${outTokens} out`,
    expandReply: "Expand reply",
    collapseReply: "Collapse reply",
    deleteExchangeTitle: "Delete this exchange",
    deleteReplyConfirmAria: "Delete this reply and your message",
    deleteReplyConfirmText: "Delete this reply and your message?",
    regenerateLabel: "Regenerate",
    tokenUsageHeading: "Token Usage",
    inputTokensLabel: "Input tokens:",
    outputTokensLabel: "Output tokens:",
    totalTokensRowLabel: "Total tokens:",
    contextWindowLabel: "Context window:",
    tokenUsageInfo:
      "Tokens are the units AI providers use to measure and bill usage. This is not a cost charged by NovelChat — any actual charges are billed directly by your AI provider. Consult their pricing page or your account with them for details. Total tokens keeps growing for as long as this chat exists; Context window is different — it's this chat's current size right now, and can grow or shrink as you send messages or delete replies.",
    staleChatGeneric: "This chat was updated elsewhere — reload it first, then try again.",
    staleChatRegenerating:
      "This chat was updated elsewhere while regenerating — reload it first, then try again.",
    excludeHint: "Click to exclude from your next message",
    includeHint: "Click to include with your next message",
    critiqueThis: "Critique this",
    alreadyCritiqued: "Already critiqued — select different text to critique again",
    attached: "Attached",
    excluded: "Excluded",
    removeSelectionAria: "Remove selected text",
    providerGoneError:
      'This session\'s provider no longer exists. Use "Edit chat" above to reconnect this chat — re-adding a provider with the same name does not automatically relink it.',
    regenerateFeedbackPlaceholder: "Optional — describe what to change, then press Regenerate",
    busyRegenerateError:
      "This chat is waiting on a reply — please wait for it to finish before regenerating another.",
    busyDeleteError:
      "This chat is waiting on a reply — please wait for it to finish before deleting a message.",
    typeOrSelectFirst: "Type a message, or select something in the document to attach, first.",
    replyCancelled: "Reply cancelled.",
    sendError: "Something went wrong sending that message.",
    regenerationCancelled: "Regeneration cancelled.",
    regenerateError: "Something went wrong regenerating that reply.",
    collapseSelectedText: "Collapse selected text",
    expandSelectedText: "Expand selected text",
    selectedTextLabel: "Selected text from the document:",
    critiqueTextLabel: "Critique text from the document:",
  },
  exportFile: {
    provider: "**Provider:**",
    model: "**Model:**",
    exported: "**Exported:**",
    you: "**You:**",
    assistant: "**Assistant:**",
    untitledEntry: "(untitled entry)",
    excludedFromContext: " (excluded from context)",
  },
  modelCombobox: {
    showModelListAria: "Show model list",
    noModelsReturned:
      "No models were returned for this key/endpoint. You can still type a model name manually.",
    fetchFailed: "Could not fetch the model list. You can still type a model name manually.",
  },
  domUtils: {
    moreInfoAria: "More info",
  },
  settings: {
    title: "Settings",
    critiquePromptLabel: "Critique prompt",
    critiquePromptInfo:
      'The instruction sent to the AI when you click "Critique this" on a selected passage. Shown here pre-filled with NovelChat\'s built-in version — edit it to change how critiques are framed.',
    revertToDefault: "Revert to default",
    languageLabel: "Language",
    // Native self-referential names (each language names itself, not "translated" into whatever
    // language is currently active) — same three values across EN/ZH/JA on purpose, same
    // convention as this settings row's own button labels/interaction. Previously these were
    // "English"/"Chinese"/"Japanese" verbatim in all three language objects — never actually
    // localized, so the row always read "Language: English / Chinese / Japanese" regardless of
    // the app's current UI language.
    languageEnglish: "English",
    languageChinese: "中文",
    languageJapanese: "日本語",
  },
};

export const ZH: Strings = {
  common: {
    save: "保存",
    cancel: "取消",
    delete: "删除",
    edit: "编辑",
    add: "添加",
    export: "导出",
    backToChat: "← 返回聊天",
    yesDelete: "确认删除",
  },
  standalone: {
    noticeBody:
      "NovelChat Web 中的所有数据，包括服务商、聊天记录和故事设定，仅保存在本台电脑的此浏览器中。不会存储在线上，也不会与 Word 插件同步。清除浏览器的网站数据，或使用隐私/无痕窗口，将永久删除这些数据。",
    noticeWarning: "请勿在公共或共用电脑上使用。",
    noticeLearnMore: "了解更多",
    noticeDismiss: "知道了",
  },
  onboarding: {
    title: "欢迎",
    body: "尚未设置任何 AI 提供商。请先添加一个即可开始聊天。",
    addProvider: "+ 添加提供商",
  },
  providerList: {
    title: "AI 提供商",
    emptyHint: "尚未配置任何提供商。",
    startNewChat: "开始新聊天",
    addProvider: "+ 添加提供商",
    deleteConfirm: (chatCount) =>
      chatCount > 0
        ? `删除此提供商？此操作无法撤销。当前有 ${chatCount} 个聊天正在使用它，删除后需要通过“编辑聊天”重新连接到其他提供商。`
        : "删除此提供商？此操作无法撤销。",
    defaultValue: "默认",
    temperature: (value) => `温度：${value}`,
    maxContext: (value, defaultKind) =>
      `最大上下文：${value}${defaultKind === "model" ? "（模型默认值）" : defaultKind === "generic" ? "（默认）" : ""}`,
    infoHeading: "让 AI 更好地为你所用",
    infoBody:
      "NovelChat 旨在辅助你的小说创作，让你与 AI 协作的流程更快、更高效。AI 的效果取决于你如何引导它。设置系统提示词、建立故事设定集，并给出清晰的指令，都能帮助你获得更好的结果。",
  },
  providerForm: {
    titleAdd: "添加提供商",
    titleEdit: "编辑提供商",
    displayName: "显示名称",
    aiService: "AI 服务",
    adapterType: "适配器类型",
    adapterOpenAiCompatible: "OpenAI 兼容",
    adapterGemini: "Gemini",
    adapterAnthropic: "Anthropic（Claude）",
    apiEndpoint: "API 端点",
    showEndpointListAria: "显示已知端点",
    apiKey: "API 密钥",
    apiKeyInfo:
      "此密钥用于让 NovelChat 代表你向该 AI 提供商发送请求并接收回复，仅保存在本机。产生的使用费用由该提供商直接收取，与 NovelChat 无关。",
    getApiKey: "获取 API 密钥",
    model: "模型",
    modelPlaceholder: "选择或输入模型",
    temperature: "温度（可选）",
    temperatureInfo:
      "控制回复的多样性，范围为 0 到 2。较低的值（如 0.3）使输出更聚焦、更一致，适合保持稳定的语气或角色声音；较高的值（如 0.9）使输出更多样、更有创意，适合头脑风暴。留空则使用模型默认值。",
    maxContext: "最大上下文（令牌数，可选）",
    maxContextInfo:
      "控制每条消息随附发送的聊天历史量——数值越大，AI 能记住的场景越多，但每条消息的成本也越高。留空时 NovelChat 会自动为你的模型选择合适的值。仅在确认模型真实上限时才手动调高，超过该上限会导致请求出错。",
    systemPrompt: "系统提示词（可选）",
    systemPromptInfo:
      "在使用此提供商的每个聊天中，每条消息发送前都会附上此提示词。可用它设定 AI 应遵循的人设、语气或语言偏好。留空则不使用系统提示词。",
    systemPromptPlaceholder: "可选——描述使用此服务商的每个聊天中，AI 应遵循的人设、语气或指令。",
    maxOutput: "最大回复长度（令牌数，可选）",
    maxOutputInfo:
      "Anthropic（Claude）要求每次请求都指定最大回复长度。留空时 NovelChat 会自动使用你所选模型的真实上限，通常无需调整。仅在你想限制回复长度以控制成本时才手动设置更低的数值。",
    modelDefault: (value) => `模型默认值：${value}`,
    genericDefault: (value) => `默认值：${value}`,
    fetchBlockedReason: "请先填写 API 端点和 API 密钥，然后点击箭头加载模型列表。",
    allFieldsRequired: "所有字段均为必填项。",
    modelNotInList: (model) =>
      `“${model}”不在该提供商已获取的模型列表中。请从下拉列表（▾）中选择一个，或重新获取以更新列表。`,
    temperatureRange: "温度必须是 0 到 2 之间的数字，或留空。",
    maxContextPositive: "最大上下文（令牌数）必须是正整数，或留空。",
    maxOutputPositive: "最大回复长度（令牌数）必须是正整数，或留空。",
    saveConfirmCost: (name) =>
      `使用此 API 密钥将向 ${name} 发送请求，可能产生使用费用，费用将根据你自己的账户和资费方案由其直接收取。NovelChat 无法查看或控制这些费用——请在继续前查看该提供商官网的最新定价。`,
    saveConfirmAgree: `点击下方的“我同意，保存提供商”即表示你确认并承担因此 API 密钥产生的一切使用费用责任。`,
    agreeSaveProvider: "我同意，保存提供商",
  },
  sessionSwitcher: {
    homeTitle: "主页",
    switchChatAria: "切换聊天",
    newChatTitle: "开始新聊天",
    newChatAria: "新聊天",
    editChatIconTitle: "编辑此聊天",
    editChatIconAria: "编辑聊天",
    deleteChatIconTitle: "删除此聊天",
    deleteChatIconAria: "删除",
    deleteChatConfirmText: "删除此聊天？你之后可以在“历史记录”中恢复它。",
    navHistory: "历史记录",
    navProviders: "提供商",
    navStoryBible: "故事设定集",
    settingsIconTitle: "设置",
    settingsIconAria: "设置",
    startNewChatTitle: "开始新聊天",
    chatNameOptional: "聊天名称（可选）",
    chatNamePlaceholder: (example) => `留空则默认使用时间戳，例如 ${example}`,
    provider: "提供商",
    model: "模型",
    modelPlaceholder: "选择或输入模型",
    startChat: "开始聊天",
    enterOrSelectModel: "请输入或选择一个模型。",
    modelNotInList: (model) =>
      `“${model}”不在已获取的模型列表中。请从下拉列表（▾）中选择一个，或重新获取以更新列表。`,
    temperature: "温度",
    temperatureInfo:
      "控制回复的多样性，范围为 0 到 2。较低的值（如 0.3）使输出更聚焦、更一致；较高的值（如 0.9）使输出更多样、更有创意。继承会跟随此聊天所用提供商的设置，即使之后更换提供商也是如此；清除则连提供商的设置也一并忽略，改用模型自身的默认值。",
    maxContext: "最大上下文（令牌数）",
    maxContextInfo:
      "控制每条消息随附发送的聊天历史量。继承会跟随此聊天所用提供商的设置，即使之后更换提供商也是如此；清除则忽略提供商设置，始终使用你模型的真实上限。若你自行设置了数值，之后更换提供商时会保留该数值——除非它超出新模型的上限，此时会自动下调以适应。",
    maxOutput: "最大回复长度（令牌数，可选）",
    maxOutputInfo:
      "Anthropic（Claude）要求每次请求都指定最大回复长度。留空则使用此聊天所用提供商的设置，该设置已是你模型的真实上限，通常无需调整。仅适用于 Anthropic 提供商。若你自行设置了数值，之后更换提供商时会重置为跟随新提供商。",
    systemPrompt: "系统提示词",
    systemPromptInfo:
      "此聊天中每条消息发送前都会附上此提示词。继承会跟随此聊天所用提供商的设置，即使之后更换提供商也是如此；清除表示此聊天完全不使用系统提示词，即使提供商本身设有系统提示词。删除你已输入的内容同样视为清除——如果想恢复跟随提供商，请点击“继承”。",
    inherit: "继承",
    clear: "清除",
    modelDefault: "模型默认值",
    genericDefault: "默认值",
    providerDefault: (value) => `提供商默认值：${value}`,
    providerDefaultPlain: "提供商默认值",
    noSystemPrompt: "无系统提示词（已清除）",
    none: "无",
    temperatureRange: "温度必须是 0 到 2 之间的数字，或留空。",
    maxContextPositive: "最大上下文（令牌数）必须是正整数，或留空。",
    maxOutputPositive: "最大回复长度（令牌数）必须是正整数，或留空。",
    editChatTitle: "编辑聊天",
    chatName: "聊天名称",
    noProvidersHint: "尚未设置任何提供商。请先添加一个，再回来重新连接此聊天。",
    chatNameRequired: "聊天名称为必填项。",
    chatNoLongerExists: "此聊天已不存在——可能已在别处被删除。",
    chatUpdatedElsewhere:
      "此聊天已在别处更新——请取消并重新打开“编辑聊天”以查看最新版本，然后重试。",
    selectProviderPlaceholder: "此聊天的提供商已被删除——请选择一个提供商",
    selectProviderFirstHint: "请先在上方选择一个提供商。",
    providerRequired: "请为此聊天选择一个提供商。",
  },
  storyBible: {
    fieldLabel: "故事设定集",
    none: "无",
    create: "+ 创建",
    edit: "编辑",
    titleNew: "新建故事设定集",
    titleEdit: "编辑故事设定集",
    nameLabel: "故事设定集名称",
    entries: "条目",
    entriesInfo:
      "你可以把所有内容放在一个条目里，也可以拆分成多个条目——怎样方便你随着故事发展维护就怎么来。不知道从哪里开始？可以让 AI 聊天帮你起草条目，再粘贴到这里修改完善。",
    tokensTotal: (value) => `约 ${value} 个令牌`,
    tokensCount: (value) => `约 ${value} 个令牌`,
    noEntries: "暂无条目。",
    addEntry: "+ 添加条目",
    entryTitle: "标题",
    entryText: "内容",
    saveEntry: "保存条目",
    includedTitle: "已纳入上下文",
    excludedTitle: "已从上下文中排除",
    includedAria: "已纳入上下文——点击以排除",
    excludedAria: "已从上下文中排除——点击以纳入",
    titleRequired: "标题为必填项。",
    entryTextRequired: "条目内容不能为空。",
    nameRequired: "故事设定集名称为必填项。",
    listTitle: "故事设定集",
    listEmptyHint: "暂无故事设定集。",
    entryCount: (n) => `${n} 个条目`,
    linkedToChats: (titles, moreCount) =>
      `已关联：${titles.join("、")}${moreCount > 0 ? ` 等还有 ${moreCount} 个` : ""}`,
    notLinkedToAnyChat: "未关联任何对话",
    addStoryBible: "+ 添加故事设定集",
    pendingEntryNote: "你有一个尚未保存的条目，请先保存或取消它再继续。",
    expandAll: "全部展开",
    linkedChatsHeading: "已关联的对话",
    linkedChatsNone: "尚无对话关联此故事设定集。",
    closedTag: "（已关闭）",
    unlink: "取消关联",
    link: "关联",
    linkSelectPlaceholder: "选择要关联的对话…",
    unlinkedOption: (chatTitle) => `${chatTitle}（未关联）`,
    linkedElsewhereOption: (chatTitle, bibleName) => `${chatTitle}（当前关联：${bibleName}）`,
    collapseAll: "全部收起",
  },
  history: {
    title: "历史记录",
    tabChats: "聊天",
    tabStoryBibles: "故事设定集",
    emptyChats:
      "暂无已删除的聊天。删除一个聊天（其标签页上的 &times;）会将其移到这里，不会丢失任何内容。",
    unknownProvider: "未知提供商",
    messageCount: (n) => `${n} 条消息`,
    purgeChatConfirm: "永久清除此聊天？此操作无法撤销。",
    emptyDeletedBibles:
      "暂无已删除的故事设定集。在“故事设定集”界面删除一个后，会移到这里，不会丢失任何内容。",
    purgeConfirm: "永久清除此故事设定集？此操作无法撤销。",
    yesPurge: "确认清除",
    recover: "恢复",
    purge: "清除",
    selectedCount: (n) => `已选择 ${n} 项`,
    selectItemAria: (title) => `选择${title}`,
    recoverSelected: "恢复所选",
    purgeSelected: "清除所选",
    purgeAll: "全部清除",
    purgeSelectedChatsConfirm: (n) => `永久清除已选择的 ${n} 个聊天？此操作无法撤销。`,
    purgeAllChatsConfirm: (n) => `永久清除全部 ${n} 个已删除的聊天？此操作无法撤销。`,
    purgeSelectedBiblesConfirm: (n) => `永久清除已选择的 ${n} 个故事设定集？此操作无法撤销。`,
    purgeAllBiblesConfirm: (n) => `永久清除全部 ${n} 个已删除的故事设定集？此操作无法撤销。`,
  },
  chat: {
    sessionTag: (provider, model, temperature) => `${provider} · ${model} · 温度：${temperature}`,
    defaultValue: "默认",
    showToolbar: "显示工具栏",
    hideToolbar: "隐藏工具栏",
    totalTokensLabel: "总令牌数：",
    tokenUsageAria: "此聊天的令牌用量",
    contextExceededBanner: "此聊天已超出上下文上限——较早的消息将不再发送给 AI。",
    dismissAria: "关闭",
    staleChatBanner:
      "此聊天已在另一个窗口中更新。请刷新以查看最新版本，或切换到其他聊天（新建或已有）。",
    reload: "刷新",
    regenerateBanner: "正在重新生成该回复——可以在下方输入需要有何不同，然后点击“重新生成”。",
    cancelRegenerateAria: "取消重新生成",
    typeMessagePlaceholder: "输入你的消息……",
    send: "发送",
    thinking: "正在思考……",
    contextExceededDivider: "已超出上下文上限——上方聊天内容已排除",
    providerChangedDivider: (name) => `提供商已切换为 ${name}`,
    responseTime: (seconds) => `响应时间：${seconds} 秒`,
    tokensInOut: (inTokens, outTokens) => `令牌数：输入 ${inTokens} / 输出 ${outTokens}`,
    expandReply: "展开回复",
    collapseReply: "收起回复",
    deleteExchangeTitle: "删除此往返消息",
    deleteReplyConfirmAria: "删除此回复及你的消息",
    deleteReplyConfirmText: "删除此回复及你的消息？",
    regenerateLabel: "重新生成",
    tokenUsageHeading: "令牌用量",
    inputTokensLabel: "输入令牌数：",
    outputTokensLabel: "输出令牌数：",
    totalTokensRowLabel: "总令牌数：",
    contextWindowLabel: "上下文窗口：",
    tokenUsageInfo:
      "令牌是 AI 提供商用来衡量和计费使用量的单位。这不是 NovelChat 收取的费用——任何实际费用都由你的 AI 提供商直接收取。详情请查看其定价页面或你的账户信息。总令牌数会随着此聊天的持续使用不断增加；上下文窗口则不同——它是此聊天当前的实际大小，会随着你发送消息或删除回复而增大或缩小。",
    staleChatGeneric: "此聊天已在别处更新——请先刷新，然后重试。",
    staleChatRegenerating: "重新生成期间此聊天已在别处更新——请先刷新，然后重试。",
    excludeHint: "点击以在下一条消息中排除",
    includeHint: "点击以在下一条消息中附带",
    critiqueThis: "点评此内容",
    alreadyCritiqued: "已点评过——请选择其他文本以再次点评",
    attached: "已附带",
    excluded: "已排除",
    removeSelectionAria: "移除已选中文本",
    providerGoneError:
      "此聊天所用的提供商已不存在。请使用上方的“编辑聊天”重新连接此聊天——重新添加同名提供商不会自动重新关联。",
    regenerateFeedbackPlaceholder: "可选——描述需要有何不同，然后点击“重新生成”",
    busyRegenerateError: "此聊天正在等待回复——请等待完成后再重新生成另一条。",
    busyDeleteError: "此聊天正在等待回复——请等待完成后再删除消息。",
    typeOrSelectFirst: "请先输入一条消息，或在文档中选中要附带的内容。",
    replyCancelled: "回复已取消。",
    sendError: "发送该消息时出错。",
    regenerationCancelled: "重新生成已取消。",
    regenerateError: "重新生成该回复时出错。",
    collapseSelectedText: "收起选中文本",
    expandSelectedText: "展开选中文本",
    selectedTextLabel: "文档中选中的文本：",
    critiqueTextLabel: "点评文档中选中的文本：",
  },
  exportFile: {
    provider: "**提供商：**",
    model: "**模型：**",
    exported: "**导出时间：**",
    you: "**你：**",
    assistant: "**助手：**",
    untitledEntry: "（无标题条目）",
    excludedFromContext: "（已从上下文中排除）",
  },
  modelCombobox: {
    showModelListAria: "显示模型列表",
    noModelsReturned: "该密钥/端点未返回任何模型。你仍可以手动输入模型名称。",
    fetchFailed: "无法获取模型列表。你仍可以手动输入模型名称。",
  },
  domUtils: {
    moreInfoAria: "更多信息",
  },
  settings: {
    title: "设置",
    critiquePromptLabel: "点评提示词",
    critiquePromptInfo:
      "点击选中文本上的“点评这段”时发送给 AI 的指令。这里预填了 NovelChat 的内置版本——可以编辑它来改变点评的框架。",
    revertToDefault: "恢复默认",
    languageLabel: "语言",
    languageEnglish: "English",
    languageChinese: "中文",
    languageJapanese: "日本語",
  },
};

export const JA: Strings = {
  common: {
    save: "保存",
    cancel: "キャンセル",
    delete: "削除",
    edit: "編集",
    add: "追加",
    export: "エクスポート",
    backToChat: "← チャットに戻る",
    yesDelete: "はい、削除します",
  },
  standalone: {
    noticeBody:
      "NovelChat Web 内のすべてのデータ（プロバイダー、チャット、ストーリーバイブルを含む）は、このパソコンのこのブラウザにのみ保存されます。オンラインには保存されず、Word アドインとも同期しません。ブラウザのサイトデータを削除するか、プライベートまたはシークレットウィンドウを使用すると、完全に削除されます。",
    noticeWarning: "公共のパソコンや共有パソコンでの使用は避けてください。",
    noticeLearnMore: "詳細を見る",
    noticeDismiss: "了解しました",
  },
  onboarding: {
    title: "ようこそ",
    body: "まだ AI プロバイダーが設定されていません。追加するとチャットを開始できます。",
    addProvider: "+ プロバイダーを追加",
  },
  providerList: {
    title: "AI プロバイダー",
    emptyHint: "プロバイダーがまだ設定されていません。",
    startNewChat: "新しいチャットを開始",
    addProvider: "+ プロバイダーを追加",
    deleteConfirm: (chatCount) =>
      chatCount > 0
        ? `このプロバイダーを削除しますか？この操作は元に戻せません。現在 ${chatCount} 件のチャットがこのプロバイダーを使用しており、削除後は「チャットを編集」から別のプロバイダーに再接続する必要があります。`
        : "このプロバイダーを削除しますか？この操作は元に戻せません。",
    defaultValue: "デフォルト",
    temperature: (value) => `温度：${value}`,
    maxContext: (value, defaultKind) =>
      `最大コンテキスト：${value}${defaultKind === "model" ? "（モデルのデフォルト）" : defaultKind === "generic" ? "（デフォルト）" : ""}`,
    infoHeading: "AI をもっと活用する",
    infoBody:
      "NovelChat は小説執筆をサポートし、AI との作業をより速く効果的にするために作られています。AI は明確に指示するほど良い結果を返します。システムプロンプトの設定、ストーリーバイブルの作成、明確な指示を書くことが、より良い結果を得る助けになります。",
  },
  providerForm: {
    titleAdd: "プロバイダーを追加",
    titleEdit: "プロバイダーを編集",
    displayName: "表示名",
    aiService: "AI サービス",
    adapterType: "アダプタータイプ",
    adapterOpenAiCompatible: "OpenAI 互換",
    adapterGemini: "Gemini",
    adapterAnthropic: "Anthropic（Claude）",
    apiEndpoint: "API エンドポイント",
    showEndpointListAria: "既知のエンドポイントを表示",
    apiKey: "API キー",
    apiKeyInfo:
      "このキーは、NovelChat があなたに代わってこの AI プロバイダーにリクエストを送信し、応答を受け取るために使用されます。このコンピューター上にのみ保存されます。利用料金が発生する場合はプロバイダーが直接請求し、NovelChat は関与しません。",
    getApiKey: "API キーを取得",
    model: "モデル",
    modelPlaceholder: "モデルを選択または入力",
    temperature: "温度（任意）",
    temperatureInfo:
      "応答のばらつきを 0 から 2 の範囲で制御します。低い値（例：0.3）はより一貫性のある焦点の絞られた出力になり、口調やキャラクターの声を一定に保つのに役立ちます。高い値（例：0.9）はより多様で創造的な出力になり、アイデア出しに向いています。空欄のままにするとモデルのデフォルト値が使用されます。",
    maxContext: "最大コンテキスト（トークン数、任意）",
    maxContextInfo:
      "各メッセージとともに送信されるこのチャットの履歴量を制御します。数値を大きくすると AI が長い場面をより多く記憶できますが、メッセージごとのコストも増加します。空欄のままにすると NovelChat が自動的にモデルに適した値を選びます。モデルの実際の上限を確実に把握している場合のみ手動で引き上げてください。上限を超えるとリクエストエラーになります。",
    systemPrompt: "システムプロンプト（任意）",
    systemPromptInfo:
      "このプロバイダーを使用するすべてのチャットで、各メッセージの前に送信されます。AI に従わせたい人物設定、口調、言語の好みを定義するために使用します。空欄のままにすると使用されません。",
    systemPromptPlaceholder:
      "任意——このプロバイダーを使うすべてのチャットで AI に従わせたい人物設定、口調、指示を記入してください。",
    maxOutput: "最大返信長（トークン数、任意）",
    maxOutputInfo:
      "Anthropic（Claude）はすべてのリクエストで最大返信長の指定を必要とします。空欄のままにすると NovelChat が自動的にモデルの実際の上限を使用するため、通常は調整不要です。返信の長さを制限してコストを管理したい場合のみ、自分で低い値を設定してください。",
    modelDefault: (value) => `モデルのデフォルト：${value}`,
    genericDefault: (value) => `デフォルト：${value}`,
    fetchBlockedReason:
      "まず API エンドポイントと API キーを入力し、矢印をクリックしてモデルを読み込んでください。",
    allFieldsRequired: "すべての項目が必須です。",
    modelNotInList: (model) =>
      `「${model}」はこのプロバイダーで取得済みのモデル一覧に含まれていません。ドロップダウン（▾）から選択するか、一覧が古い場合は再取得してください。`,
    temperatureRange: "温度は 0 から 2 の間の数値、または空欄にしてください。",
    maxContextPositive: "最大コンテキスト（トークン数）は正の整数、または空欄にしてください。",
    maxOutputPositive: "最大返信長（トークン数）は正の整数、または空欄にしてください。",
    saveConfirmCost: (name) =>
      `このAPIキーを使用すると、${name} にリクエストが送信され、利用料金が発生する場合があります。料金はあなた自身のアカウントと料金プランに基づき、${name} が直接請求します。NovelChat はこれらの料金を把握・管理することはできません。利用前に、プロバイダー公式サイトで最新の料金をご確認ください。`,
    saveConfirmAgree:
      "下の「同意してプロバイダーを保存」をクリックすることで、この API キーによって発生するすべての利用料金について責任を負うことに同意したものとみなされます。",
    agreeSaveProvider: "同意してプロバイダーを保存",
  },
  sessionSwitcher: {
    homeTitle: "ホーム",
    switchChatAria: "チャットを切り替え",
    newChatTitle: "新しいチャットを開始",
    newChatAria: "新しいチャット",
    editChatIconTitle: "このチャットを編集",
    editChatIconAria: "チャットを編集",
    deleteChatIconTitle: "このチャットを削除",
    deleteChatIconAria: "削除",
    deleteChatConfirmText: "このチャットを削除しますか？後で履歴から復元できます。",
    navHistory: "履歴",
    navProviders: "プロバイダー",
    navStoryBible: "ストーリーバイブル",
    settingsIconTitle: "設定",
    settingsIconAria: "設定",
    startNewChatTitle: "新しいチャットを開始",
    chatNameOptional: "チャット名（任意）",
    chatNamePlaceholder: (example) => `空欄の場合はタイムスタンプが使用されます（例：${example}）`,
    provider: "プロバイダー",
    model: "モデル",
    modelPlaceholder: "モデルを選択または入力",
    startChat: "チャットを開始",
    enterOrSelectModel: "モデルを入力または選択してください。",
    modelNotInList: (model) =>
      `「${model}」は取得済みのモデル一覧に含まれていません。ドロップダウン（▾）から選択するか、一覧が古い場合は再取得してください。`,
    temperature: "温度",
    temperatureInfo:
      "応答のばらつきを 0 から 2 の範囲で制御します。低い値（例：0.3）はより焦点の絞られた一貫性のある出力になり、高い値（例：0.9）はより多様で創造的な出力になります。「継承」はプロバイダーを切り替えた後もこのチャットのプロバイダー設定に従います。「クリア」はプロバイダー設定も無視し、モデル自体のデフォルト値を使用します。",
    maxContext: "最大コンテキスト（トークン数）",
    maxContextInfo:
      "各メッセージとともに送信されるこのチャットの履歴量を制御します。「継承」はプロバイダーを切り替えた後もこのチャットのプロバイダー設定に従います。「クリア」はプロバイダー設定を無視し、常にモデルの実際の上限を使用します。自分で数値を設定した場合、後でプロバイダーを切り替えてもその値は保持されます。ただし新しいモデルにとって高すぎる場合は、自動的に収まる値まで引き下げられます。",
    maxOutput: "最大返信長（トークン数、任意）",
    maxOutputInfo:
      "Anthropic（Claude）はすべてのリクエストで最大返信長の指定を必要とします。空欄のままにするとこのチャットのプロバイダー設定（すでにモデルの実際の上限）が使用されるため、通常は調整不要です。Anthropic のプロバイダーにのみ適用されます。自分で数値を設定した場合、後でプロバイダーを切り替えると新しいプロバイダーに従う設定にリセットされます。",
    systemPrompt: "システムプロンプト",
    systemPromptInfo:
      "このチャットの各メッセージの前に送信されます。「継承」はプロバイダーを切り替えた後もこのチャットのプロバイダー設定に従います。「クリア」はプロバイダーにシステムプロンプトが設定されていても、このチャットでは一切使用しないことを意味します。入力した内容を削除することも「クリア」として扱われます。プロバイダー設定に従う状態に戻したい場合は「継承」をクリックしてください。",
    inherit: "継承",
    clear: "クリア",
    modelDefault: "モデルのデフォルト",
    genericDefault: "デフォルト",
    providerDefault: (value) => `プロバイダーのデフォルト：${value}`,
    providerDefaultPlain: "プロバイダーのデフォルト",
    noSystemPrompt: "システムプロンプトなし（クリア済み）",
    none: "なし",
    temperatureRange: "温度は 0 から 2 の間の数値、または空欄にしてください。",
    maxContextPositive: "最大コンテキスト（トークン数）は正の整数、または空欄にしてください。",
    maxOutputPositive: "最大返信長（トークン数）は正の整数、または空欄にしてください。",
    editChatTitle: "チャットを編集",
    chatName: "チャット名",
    noProvidersHint:
      "プロバイダーが設定されていません。まず 1 つ追加してから、このチャットを再接続してください。",
    chatNameRequired: "チャット名は必須です。",
    chatNoLongerExists: "このチャットは既に存在しません — 他の場所で削除された可能性があります。",
    chatUpdatedElsewhere:
      "このチャットは他の場所で更新されました — 一度キャンセルして「チャットを編集」を開き直し、最新版を確認してから再試行してください。",
    selectProviderPlaceholder:
      "このチャットのプロバイダーは削除されました — プロバイダーを選択してください",
    selectProviderFirstHint: "まず上のプロバイダーを選択してください。",
    providerRequired: "このチャットのプロバイダーを選択してください。",
  },
  storyBible: {
    fieldLabel: "ストーリーバイブル",
    none: "なし",
    create: "+ 作成",
    edit: "編集",
    titleNew: "新規ストーリーバイブル",
    titleEdit: "ストーリーバイブルを編集",
    nameLabel: "ストーリーバイブル名",
    entries: "項目",
    entriesInfo:
      "すべての内容を 1 つの項目にまとめても、複数の項目に分けても構いません — 物語の成長に合わせて管理しやすい方法を選んでください。何から始めればよいか分からない場合は、AI チャットに項目の下書きを手伝ってもらい、それをここに貼り付けて調整してください。",
    tokensTotal: (value) => `合計 約${value} トークン`,
    tokensCount: (value) => `約${value} トークン`,
    noEntries: "項目はまだありません。",
    addEntry: "+ 項目を追加",
    entryTitle: "タイトル",
    entryText: "内容",
    saveEntry: "項目を保存",
    includedTitle: "コンテキストに含める",
    excludedTitle: "コンテキストから除外",
    includedAria: "コンテキストに含まれています — クリックで除外",
    excludedAria: "コンテキストから除外されています — クリックで含める",
    titleRequired: "タイトルは必須です。",
    entryTextRequired: "項目の内容を空にすることはできません。",
    nameRequired: "ストーリーバイブル名は必須です。",
    listTitle: "ストーリーバイブル",
    listEmptyHint: "ストーリーバイブルはまだありません。",
    entryCount: (n) => `${n} 件の項目`,
    linkedToChats: (titles, moreCount) =>
      `関連付け先：${titles.join("、")}${moreCount > 0 ? ` 他${moreCount}件` : ""}`,
    notLinkedToAnyChat: "どのチャットにも関連付けられていません",
    addStoryBible: "+ ストーリーバイブルを追加",
    pendingEntryNote: "未保存の項目があります。続ける前に保存またはキャンセルしてください。",
    expandAll: "すべて展開",
    linkedChatsHeading: "関連付けられたチャット",
    linkedChatsNone: "このストーリーバイブルに関連付けられたチャットはまだありません。",
    closedTag: "（終了済み）",
    unlink: "関連付けを解除",
    link: "関連付け",
    linkSelectPlaceholder: "関連付けるチャットを選択…",
    unlinkedOption: (chatTitle) => `${chatTitle}（未関連付け）`,
    linkedElsewhereOption: (chatTitle, bibleName) => `${chatTitle}（現在の関連付け：${bibleName}）`,
    collapseAll: "すべて折りたたむ",
  },
  history: {
    title: "履歴",
    tabChats: "チャット",
    tabStoryBibles: "ストーリーバイブル",
    emptyChats:
      "削除済みのチャットはまだありません。チャットを削除する（タブの &times;）と、内容を失うことなくここに移動します。",
    unknownProvider: "不明なプロバイダー",
    messageCount: (n) => `${n} 件のメッセージ`,
    purgeChatConfirm: "このチャットを完全に削除しますか？この操作は元に戻せません。",
    emptyDeletedBibles:
      "削除済みのストーリーバイブルはありません。ストーリーバイブル画面で削除すると、内容を失うことなくここに移動します。",
    purgeConfirm: "このストーリーバイブルを完全に削除しますか？この操作は元に戻せません。",
    yesPurge: "はい、完全に削除します",
    recover: "復元",
    purge: "完全に削除",
    selectedCount: (n) => `${n} 件選択中`,
    selectItemAria: (title) => `${title}を選択`,
    recoverSelected: "選択項目を復元",
    purgeSelected: "選択項目を完全に削除",
    purgeAll: "すべて完全に削除",
    purgeSelectedChatsConfirm: (n) =>
      `選択した ${n} 件のチャットを完全に削除しますか？この操作は元に戻せません。`,
    purgeAllChatsConfirm: (n) =>
      `削除済みの ${n} 件のチャットすべてを完全に削除しますか？この操作は元に戻せません。`,
    purgeSelectedBiblesConfirm: (n) =>
      `選択した ${n} 件のストーリーバイブルを完全に削除しますか？この操作は元に戻せません。`,
    purgeAllBiblesConfirm: (n) =>
      `削除済みの ${n} 件のストーリーバイブルすべてを完全に削除しますか？この操作は元に戻せません。`,
  },
  chat: {
    sessionTag: (provider, model, temperature) => `${provider}・${model}・温度：${temperature}`,
    defaultValue: "デフォルト",
    showToolbar: "ツールバーを表示",
    hideToolbar: "ツールバーを隠す",
    totalTokensLabel: "総トークン数：",
    tokenUsageAria: "このチャットのトークン使用量",
    contextExceededBanner:
      "このチャットはコンテキスト上限を超えています — それより古いメッセージは AI に送信されません。",
    dismissAria: "閉じる",
    staleChatBanner:
      "このチャットは別のウィンドウで更新されました。最新版を見るには再読み込みするか、他のチャット（新規または既存）に切り替えてください。",
    reload: "再読み込み",
    regenerateBanner:
      "その返信を再生成しています — 下に変更したい点を入力してから「再生成」を押すこともできます。",
    cancelRegenerateAria: "再生成をキャンセル",
    typeMessagePlaceholder: "メッセージを入力…",
    send: "送信",
    thinking: "考え中…",
    contextExceededDivider: "コンテキスト上限を超えました — 上のチャット内容は除外されています",
    providerChangedDivider: (name) => `プロバイダーが ${name} に変更されました`,
    responseTime: (seconds) => `応答時間：${seconds}秒`,
    tokensInOut: (inTokens, outTokens) => `トークン数：入力 ${inTokens} / 出力 ${outTokens}`,
    expandReply: "返信を展開",
    collapseReply: "返信を折りたたむ",
    deleteExchangeTitle: "このやり取りを削除",
    deleteReplyConfirmAria: "この返信とあなたのメッセージを削除",
    deleteReplyConfirmText: "この返信とあなたのメッセージを削除しますか？",
    regenerateLabel: "再生成",
    tokenUsageHeading: "トークン使用量",
    inputTokensLabel: "入力トークン数：",
    outputTokensLabel: "出力トークン数：",
    totalTokensRowLabel: "総トークン数：",
    contextWindowLabel: "コンテキストウィンドウ：",
    tokenUsageInfo:
      "トークンは AI プロバイダーが使用量の計測と課金に用いる単位です。これは NovelChat が請求する料金ではありません — 実際の料金はすべてあなたの AI プロバイダーが直接請求します。詳細はプロバイダーの料金ページまたはアカウント情報をご確認ください。総トークン数はこのチャットが存在する限り増え続けます。コンテキストウィンドウはこれとは異なり、このチャットの現在のサイズを示すもので、メッセージの送信や返信の削除によって増減します。",
    staleChatGeneric:
      "このチャットは他の場所で更新されました — 先に再読み込みしてから再試行してください。",
    staleChatRegenerating:
      "再生成中にこのチャットが他の場所で更新されました — 先に再読み込みしてから再試行してください。",
    excludeHint: "クリックすると次のメッセージから除外します",
    includeHint: "クリックすると次のメッセージに含めます",
    critiqueThis: "これを点評する",
    alreadyCritiqued: "すでに点評済みです — 再度点評するには別のテキストを選択してください",
    attached: "添付済み",
    excluded: "除外済み",
    removeSelectionAria: "選択したテキストを削除",
    providerGoneError:
      "このセッションのプロバイダーは既に存在しません。上の「チャットを編集」からこのチャットを再接続してください — 同名のプロバイダーを再追加しても自動的には再関連付けされません。",
    regenerateFeedbackPlaceholder: "任意 — 変更したい点を記入してから「再生成」を押してください",
    busyRegenerateError: "このチャットは返信待ちです — 完了してから再生成してください。",
    busyDeleteError: "このチャットは返信待ちです — 完了してからメッセージを削除してください。",
    typeOrSelectFirst: "まずメッセージを入力するか、文書内で添付するテキストを選択してください。",
    replyCancelled: "返信をキャンセルしました。",
    sendError: "メッセージの送信中に問題が発生しました。",
    regenerationCancelled: "再生成をキャンセルしました。",
    regenerateError: "返信の再生成中に問題が発生しました。",
    collapseSelectedText: "選択したテキストを折りたたむ",
    expandSelectedText: "選択したテキストを展開",
    selectedTextLabel: "文書内で選択されたテキスト：",
    critiqueTextLabel: "文書内で選択された点評対象のテキスト：",
  },
  exportFile: {
    provider: "**プロバイダー：**",
    model: "**モデル：**",
    exported: "**エクスポート日時：**",
    you: "**あなた：**",
    assistant: "**アシスタント：**",
    untitledEntry: "（無題の項目）",
    excludedFromContext: "（コンテキストから除外）",
  },
  modelCombobox: {
    showModelListAria: "モデル一覧を表示",
    noModelsReturned:
      "このキー／エンドポイントではモデルが返されませんでした。モデル名を手動で入力することもできます。",
    fetchFailed: "モデル一覧を取得できませんでした。モデル名を手動で入力することもできます。",
  },
  domUtils: {
    moreInfoAria: "詳細情報",
  },
  settings: {
    title: "設定",
    critiquePromptLabel: "点評プロンプト",
    critiquePromptInfo:
      "選択したテキストで「これを点評する」をクリックした際に AI へ送信される指示です。ここには NovelChat の標準版があらかじめ入力されています — 編集すると点評の枠組みを変更できます。",
    revertToDefault: "デフォルトに戻す",
    languageLabel: "言語",
    languageEnglish: "English",
    languageChinese: "中文",
    languageJapanese: "日本語",
  },
};

export function getStrings(): Strings {
  const lang = getLanguage();
  return lang === "zh" ? ZH : lang === "ja" ? JA : EN;
}

// Markup for a standalone EN/CH/JP toggle, reusable anywhere a second, more prominent instance is
// wanted beyond the footer's own static one (taskpane.html) — currently providerList.ts's empty
// state ("no providers yet" reads like initial setup, same as onboarding.ts, so it gets the same
// prominent language choice rather than relying on the easy-to-miss footer). Shares the footer's
// own .footer-lang-btn styling and the data-lang attribute both this and the footer buttons key
// off of (see wireLanguageToggleButtons/updateLanguageToggleButtons below) — deliberately not
// unique ids, since more than one instance can be on screen/in the DOM at once (footer + this).
export function languageToggleHtml(): string {
  return (
    `<div class="lang-toggle-inline">` +
    `<button type="button" class="footer-lang-btn" data-lang="en">EN</button> / ` +
    `<button type="button" class="footer-lang-btn" data-lang="zh">CH</button> / ` +
    `<button type="button" class="footer-lang-btn" data-lang="ja">JP</button>` +
    `</div>`
  );
}

// Refreshes every EN/CH toggle button in the whole document (not just one scope) — there can be
// more than one instance live at once (the static footer plus e.g. providerList.ts's empty-state
// one), and all of them need to agree on which language is currently active regardless of which
// one was actually clicked. Called once at boot and again after every language switch.
export function updateLanguageToggleButtons(): void {
  const lang = getLanguage();
  document.querySelectorAll<HTMLElement>("[data-lang]").forEach((btn) => {
    btn.classList.toggle("is-active-lang", btn.dataset.lang === lang);
  });
}

// Wires every EN/CH toggle button found within `scope` — the footer (scope: document.body, wired
// once at boot in taskpane.ts) or a dynamically-rendered instance like providerList.ts's empty
// state (scope: that screen's own container, re-wired on every render, same convention as
// wireInfoIcons elsewhere in this app). `onSwitch` is the caller's own "now re-render whatever
// needs to reflect the new language" callback — taskpane.ts replays the last screen; providerList
// just calls its own onChanged handler to re-render itself.
export function wireLanguageToggleButtons(scope: HTMLElement, onSwitch: () => void): void {
  scope.querySelectorAll<HTMLButtonElement>("[data-lang]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const lang = btn.dataset.lang;
      setLanguage(lang === "zh" || lang === "ja" ? lang : "en");
      updateLanguageToggleButtons();
      onSwitch();
    });
  });
}
